import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, HeartHandshake, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Simple poll-based chat + prayer rail for /tv/live.
// Guests can join by typing a display name — no account required.
export default function LiveChat() {
  const [name, setName] = useState(() => localStorage.getItem("hbtv_chat_name") || "");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const lastAtRef = useRef(null);

  // Initial load + polling every 5s
  useEffect(() => {
    let cancelled = false;
    const load = async (since) => {
      try {
        const q = since ? `?since=${encodeURIComponent(since)}` : "";
        const { data } = await api.get(`/tv/chat${q}`);
        if (cancelled || !Array.isArray(data)) return;
        setMessages((prev) => {
          const merged = since ? [...prev, ...data] : data;
          // dedupe by id, cap to last 120
          const seen = new Set();
          const deduped = [];
          for (const m of merged) {
            if (m.id && !seen.has(m.id)) {
              seen.add(m.id);
              deduped.push(m);
            }
          }
          return deduped.slice(-120);
        });
        if (data.length) lastAtRef.current = data[data.length - 1].created_at;
      } catch {
        /* ignore polling errors — will retry next tick */
      }
    };
    load();
    const id = setInterval(() => load(lastAtRef.current), 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedMsg = message.trim();
    if (trimmedName.length < 1) {
      toast.error("Add a display name to join the chat.");
      return;
    }
    if (trimmedMsg.length < 1) return;
    setBusy(true);
    try {
      localStorage.setItem("hbtv_chat_name", trimmedName);
      const { data } = await api.post("/tv/chat", { name: trimmedName, message: trimmedMsg });
      // Optimistic append (dedupe by id will drop duplicates from next poll)
      setMessages((prev) => [...prev, data]);
      lastAtRef.current = data.created_at;
      setMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message");
    } finally {
      setBusy(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col border border-gold-500/25 bg-ink-paper/40 h-[520px] max-h-[70vh]" data-testid="tv-live-chat">
      <div className="flex items-center gap-2 border-b border-gold-500/20 px-4 py-3">
        <MessageCircle size={14} className="text-gold-400" />
        <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400">Live Chat & Prayer</p>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> live
        </span>
      </div>

      <div
        ref={listRef}
        data-testid="tv-live-chat-messages"
        className="flex-1 overflow-y-auto px-3 py-3 space-y-2 scrollbar-thin scrollbar-thumb-gold-500/20"
      >
        {messages.length === 0 ? (
          <p className="text-white/40 text-xs italic text-center pt-8" data-testid="tv-live-chat-empty">
            Be the first to say hello — the family is watching.
          </p>
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                data-testid="tv-live-chat-msg"
                className={`px-3 py-2 border ${
                  m.is_prayer
                    ? "border-gold-500/40 bg-gold-500/[0.06]"
                    : "border-white/10 bg-ink/40"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {m.is_prayer && <HeartHandshake size={11} className="text-gold-400" />}
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-gold-300">
                    {m.name}
                  </p>
                  <span className="text-[9px] text-white/40 ml-auto">{formatTime(m.created_at)}</span>
                </div>
                <p className="text-sm text-white/85 leading-snug break-words">{m.message}</p>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      <form onSubmit={submit} className="border-t border-gold-500/20 p-3 space-y-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 40))}
          placeholder="Your name"
          data-testid="tv-live-chat-name"
          maxLength={40}
          className="w-full bg-ink border border-white/10 focus:border-gold-500/60 text-sm text-white placeholder:text-white/30 px-3 py-1.5 outline-none"
        />
        <div className="flex gap-2">
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 400))}
            placeholder="Say a word of encouragement or start with 'Prayer: …'"
            data-testid="tv-live-chat-input"
            maxLength={400}
            className="flex-1 bg-ink border border-white/10 focus:border-gold-500/60 text-sm text-white placeholder:text-white/30 px-3 py-2 outline-none"
          />
          <button
            type="submit"
            disabled={busy || !message.trim() || !name.trim()}
            data-testid="tv-live-chat-send"
            className="px-3 bg-gold-500 hover:bg-gold-400 disabled:opacity-40 disabled:cursor-not-allowed text-ink flex items-center justify-center transition-colors"
          >
            <Send size={14} />
          </button>
        </div>
        <p className="text-[10px] text-white/40">
          Tip: begin with <span className="text-gold-400">Prayer:</span> to add your request to the prayer wall.
        </p>
      </form>
    </div>
  );
}
